import { css } from '@emotion/react';

import ClickableContainer from 'components/clickable-container';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import NvIcon from 'shared/components/nv-icon';
import { black, primary, white } from 'styles/global_defaults/colors';
import { textSmallFontSize } from 'styles/global_defaults/fonts';
import { standardSpacing } from 'styles/global_defaults/scaffolding';

export enum InitialState {
  COLLAPSED,
  EXPANDED,
}

type PanelStateProps = {
  initialState?: InitialState;
  persistState?: boolean;
  localStorageKey?: string;
};

type CollapsiblePanelProps = {
  title: string | React.ReactElement;
  children: React.ReactElement;
  iconSize?: string;
  dataQa?: string;
  dataQaId?: string;
  isNested?: boolean;
  panelState?: PanelStateProps;
  className?: string;
  ariaLabel?: string;
};

const styles = (isNested?: boolean) => css`
  border-radius: 10px;
  padding: 24px ${standardSpacing}px;
  background-color: ${white};

  .collapsible-panel-header {
    cursor: pointer;
    color: ${black};
  }

  ${isNested
    ? css`
        padding: 0;
        .collapsible-panel-header {
          font-size: ${textSmallFontSize}px;
        }
      `
    : css`
        &:hover {
          box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.1);
        }
        .collapsible-panel-header {
          &:hover {
            color: ${primary};
          }
        }
      `}

  &.show-gradient {
    background: linear-gradient(91.45deg, rgba(247, 251, 232, 0.3) -2.56%, rgba(220, 253, 253, 0.3) 134.69%), #FFFFFF;
    transition: background 0.3s;
  }
`;

const getPanelState = (initialState: InitialState, key: string) => {
  const currentState = localStorage.getItem(key);
  if (currentState) {
    return currentState === 'true';
  }
  return initialState === InitialState.EXPANDED;
};

const setPanelStateInLocalStorage = (key: string, newState: boolean) => localStorage.setItem(key, `${newState}`);

const CollapsiblePanel = forwardRef(
  ({
    title,
    children,
    iconSize = 'small',
    dataQa,
    dataQaId,
    isNested = false,
    panelState,
    className,
    ariaLabel,
  }: CollapsiblePanelProps,
  ref) => {
    const {
      initialState = InitialState.EXPANDED,
      persistState = false,
      localStorageKey = '',
    } = panelState || {};
    const [isExpanded, setIsExpanded] = useState<boolean>(
      persistState
        ? getPanelState(initialState, localStorageKey)
        : initialState === InitialState.EXPANDED,
    );

    const togglePanelState = () => {
      setIsExpanded((currentState) => {
        const newState = !currentState;
        if (persistState) {
          setPanelStateInLocalStorage(localStorageKey, newState);
        }
        return newState;
      });
    };

    const setPanelState = (newState: boolean) => {
      setIsExpanded(newState);
      if (persistState) {
        setPanelStateInLocalStorage(localStorageKey, newState);
      }
    };

    useImperativeHandle(ref, () => ({
      setPanelState,
    }));

    return (
      <div css={styles(isNested)} className={`${className} collapsible-panel`}>
        <ClickableContainer
          className='heading-5 collapsible-panel-header d-flex align-items-center justify-content-between'
          onClick={togglePanelState}
          data-qa={dataQa}
          data-qa-id={dataQaId}
          aria-label={ariaLabel}
          aria-expanded={isExpanded}
        >
          <div className='text-truncate'>{title}</div>
          <NvIcon
            icon={isExpanded ? 'arrow-down' : 'arrow-right'}
            size={iconSize}
          />
        </ClickableContainer>
        <Collapse in={isExpanded}>
          <div>{children}</div>
        </Collapse>
      </div>
    );
  },
);

export default CollapsiblePanel;
